//frontend/src/components/MapView.js
import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../utils/apiHelper';
import '../Mapview.css';

import laoBoundaryDefault from '../assets/GeoJSON/lao_boundary.json';
import laoBoundaryWithRegions from '../assets/GeoJSON/lao_boundary_with_regions.json';

import greenIconUrl from '../assets/markers/marker-icon-green.png';
import redIconUrl from '../assets/markers/marker-icon-red.png';
import blueIconUrl from '../assets/markers/marker-icon-blue.png';
import goldenIconUrl from '../assets/markers/marker-icon-gold.png';
import violetIconUrl from '../assets/markers/marker-icon-violet.png';
import shadowIconUrl from '../assets/markers/marker-shadow.png';

const greenIcon = new L.Icon({
    iconUrl: greenIconUrl, shadowUrl: shadowIconUrl,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});
const redIcon = new L.Icon({
    iconUrl: redIconUrl, shadowUrl: shadowIconUrl,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});
const blueIcon = new L.Icon({
    iconUrl: blueIconUrl, shadowUrl: shadowIconUrl,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});
const goldenIcon = new L.Icon({
    iconUrl: goldenIconUrl, shadowUrl: shadowIconUrl,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});
const violetIcon = new L.Icon({
    iconUrl: violetIconUrl, shadowUrl: shadowIconUrl,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const MAP_THEMES = [
    {
        value: 'osm',
        label: 'OSM Standard',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    },
    {
        value: 'carto-light',
        label: 'Carto Light',
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://www.carto.com/">CARTO</a>'
    },
    {
        value: 'carto-voyager',
        label: 'Carto Voyager',
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://www.carto.com/">CARTO</a>'
    },
    {
        value: 'osm-hot',
        label: 'OSM HOT',
        url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    },
    {
        value: 'esri',
        label: 'Esri World Street',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    },
    {
        value: 'blank',
        label: 'Blank (Laos outline only)',
        url: null,
        attribution: ''
    },
];

const FitLaosBounds = ({ geojson, triggerKey }) => {
    const map = useMap();
    useEffect(() => {
        map.fitBounds(L.geoJSON(geojson).getBounds());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerKey]);
    return null;
};

const VIEW_MODES = [
    { val: 'activity', label_en: 'Infrastructure/Access track implemented', label_la: 'ພື້ນຖານໂຄງລ່າງທີ່ໄດ້ຈັດຕັ້ງປະຕິບັດສຳເລັດແລ້ວໃນ AFN2' },
    { val: 'villages', label_en: 'All AFN2 target villages',  label_la: 'ບ້ານເປົ້າໝາຍທັງໝົດໃນໂຄງການ AFN2'  },
];

const MapView = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lang, setLang] = useState('LA');
    const [mapTheme, setMapTheme] = useState('osm');
    const [boundaryType, setBoundaryType] = useState('default');
    const [viewMode, setViewMode] = useState('activity');
    const [filterProvince, setFilterProvince] = useState('');
    const [filterDistrict, setFilterDistrict] = useState('');
    const [targetVillages, setTargetVillages] = useState([]);
    const [loadingTargetVillages, setLoadingTargetVillages] = useState(false);

    // Fetch activity data whenever language changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/api/mapView/getMapData?lang=${lang}`);
                if (res.data.success) setProjects(res.data.data);
            } catch (err) {
                console.error('Error fetching map data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [lang]);

    // Fetch target villages whenever the villages view is active or language changes.
    // Reset province/district filters on refetch because province_name keys change with language.
    useEffect(() => {
        if (viewMode !== 'villages') return;
        setFilterProvince('');
        setFilterDistrict('');
        const fetchTargetVillages = async () => {
            setLoadingTargetVillages(true);
            try {
                const res = await api.get(`/api/mapView/getTargetVillages?lang=${lang}`);
                if (res.data.success) setTargetVillages(res.data.data);
            } catch (err) {
                console.error('Error fetching target villages:', err);
            } finally {
                setLoadingTargetVillages(false);
            }
        };
        fetchTargetVillages();
    }, [viewMode, lang]);

    // Switch view mode and reset province/district filters
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        setFilterProvince('');
        setFilterDistrict('');
    };

    // Province options differ by view mode.
    // For villages mode: deduplicate by province_name (province_id is not a shared province key).
    const availableProvinces = useMemo(() => {
        const seen = new Set();
        const result = [];
        if (viewMode === 'villages') {
            for (const v of targetVillages) {
                const name = v.province_name || '';
                if (name && !seen.has(name)) {
                    seen.add(name);
                    result.push({ code: name, label: name });
                }
            }
        } else {
            for (const p of projects) {
                if (p.province_code && !seen.has(p.province_code)) {
                    seen.add(p.province_code);
                    result.push({ code: p.province_code, label: p.province_label || p.province_code });
                }
            }
        }
        return result.sort((a, b) => a.label.localeCompare(b.label));
    }, [projects, targetVillages, viewMode]);

    // District options differ by view mode and selected province.
    // For villages mode: filter by province_name, deduplicate districts by district_name.
    const availableDistricts = useMemo(() => {
        const seen = new Set();
        const result = [];
        if (viewMode === 'villages') {
            for (const v of targetVillages) {
                if (filterProvince && v.province_name !== filterProvince) continue;
                const name = v.district_name || '';
                if (name && !seen.has(name)) {
                    seen.add(name);
                    result.push({ code: name, label: name });
                }
            }
        } else {
            for (const p of projects) {
                if (filterProvince && p.province_code !== filterProvince) continue;
                if (p.district_code && !seen.has(p.district_code)) {
                    seen.add(p.district_code);
                    result.push({ code: p.district_code, label: p.district_label || p.district_code });
                }
            }
        }
        return result.sort((a, b) => a.label.localeCompare(b.label));
    }, [projects, targetVillages, viewMode, filterProvince]);

    const filteredProjects = useMemo(() => {
        return projects.filter(p => {
            if (filterProvince && p.province_code !== filterProvince) return false;
            if (filterDistrict && p.district_code !== filterDistrict) return false;
            return true;
        });
    }, [projects, filterProvince, filterDistrict]);

    const filteredVillages = useMemo(() => {
        return targetVillages.filter(v => {
            if (filterProvince && v.province_name !== filterProvince) return false;
            if (filterDistrict && v.district_name !== filterDistrict) return false;
            return true;
        });
    }, [targetVillages, filterProvince, filterDistrict]);

    const currentTheme = MAP_THEMES.find(t => t.value === mapTheme) || MAP_THEMES[0];
    const currentBoundary = boundaryType === 'default' ? laoBoundaryDefault : laoBoundaryWithRegions;

    const isLoading = viewMode === 'activity' ? loading : loadingTargetVillages;
    const totalCount  = viewMode === 'activity' ? projects.length        : targetVillages.length;
    const shownCount  = viewMode === 'activity' ? filteredProjects.length : filteredVillages.length;
    const countLabel  = viewMode === 'activity'
        ? (lang === 'LA' ? 'ໂຄງການ' : 'projects')
        : (lang === 'LA' ? 'ບ້ານ'    : 'villages');

    return (
        <div className="d-flex" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>

            {/* Left panel */}
            <div className="gme-panel">

                {/* Header */}
                <div className="gme-header">
                    <div className="gme-header-title">Geo M&amp;E</div>
                    {/* <div className="gme-header-badge">AFN-II Project</div> */}
                    {/* <div className="gme-header-subtitle">
                        Utilizing GIS mapping to accurately identify project areas, visualise results and strengthen monitoring &amp; evaluation
                    </div> */}
                </div>

                {/* Language */}
                <div className="gme-section">
                    <div className="gme-section-label">Language</div>
                    <div className="gme-pill-group">
                        {[{ val: 'LA', label: 'ພາສາລາວ' }, { val: 'EN', label: 'English' }].map(o => (
                            <button
                                key={o.val}
                                className={`gme-pill${lang === o.val ? ' active' : ''}`}
                                onClick={() => setLang(o.val)}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* View Mode */}
                <div className="gme-section">
                    <div className="gme-section-label">{lang === 'LA' ? 'ສະແດງ' : 'Show'}</div>
                    {VIEW_MODES.map(o => (
                        <div
                            key={o.val}
                            className={`gme-mode-card${viewMode === o.val ? ' active' : ''}`}
                            onClick={() => handleViewModeChange(o.val)}
                        >
                            <div className="gme-mode-radio">
                                <div className="gme-mode-radio-dot" />
                            </div>
                            <div className="gme-mode-label">
                                {lang === 'LA' ? o.label_la : o.label_en}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Map Theme */}
                <div className="gme-section">
                    <div className="gme-section-label">Map Theme</div>
                    <select
                        className="gme-select"
                        value={mapTheme}
                        onChange={e => setMapTheme(e.target.value)}
                    >
                        {MAP_THEMES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                </div>

                {/* Boundary */}
                <div className="gme-section">
                    <div className="gme-section-label">Boundary</div>
                    <div className="gme-pill-group">
                        {[
                            { val: 'default', label_en: 'Country outline', label_la: 'ຂອບເຂດປະເທດ' },
                            { val: 'regions',  label_en: 'Regions',         label_la: 'ຂອບເຂດແຂວງ'         },
                        ].map(o => (
                            <button
                                key={o.val}
                                className={`gme-pill${boundaryType === o.val ? ' active' : ''}`}
                                onClick={() => setBoundaryType(o.val)}
                            >
                                {lang === 'LA' ? o.label_la : o.label_en}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Province filter */}
                <div className="gme-section">
                    <div className="gme-section-label">
                        {lang === 'LA' ? 'ແຂວງ' : 'Province'}
                    </div>
                    <select
                        className="gme-select"
                        value={filterProvince}
                        onChange={e => { setFilterProvince(e.target.value); setFilterDistrict(''); }}
                    >
                        <option value="">{lang === 'LA' ? '— ທຸກແຂວງ —' : '— All Provinces —'}</option>
                        {availableProvinces.map(p => (
                            <option key={p.code} value={p.code}>{p.label}</option>
                        ))}
                    </select>
                </div>

                {/* District filter */}
                <div className="gme-section">
                    <div className="gme-section-label">
                        {lang === 'LA' ? 'ເມືອງ' : 'District'}
                    </div>
                    <select
                        className="gme-select"
                        value={filterDistrict}
                        onChange={e => setFilterDistrict(e.target.value)}
                        disabled={availableDistricts.length === 0}
                    >
                        <option value="">{lang === 'LA' ? '— ທຸກເມືອງ —' : '— All Districts —'}</option>
                        {availableDistricts.map(d => (
                            <option key={d.code} value={d.code}>{d.label}</option>
                        ))}
                    </select>
                </div>

                {/* Count badge */}
                <div className="gme-count-wrap">
                    <div className={`gme-count${isLoading ? ' loading' : ''}`}>
                        {isLoading
                            ? (lang === 'LA' ? 'ກຳລັງໂຫລດ...' : 'Loading...')
                            : `${lang === 'LA' ? 'ສະແດງ' : 'Showing'} ${shownCount} / ${totalCount} ${countLabel}`
                        }
                    </div>
                </div>
            </div>

            {/* Right panel: map */}
            <div style={{ flexGrow: 1, overflow: 'hidden' }}>
                <MapContainer
                    style={{ height: '75vh', width: '100%' }}
                    minZoom={6}
                    maxZoom={18}
                    maxBounds={[[6, 92], [30, 115]]}
                >
                    {currentTheme.url && (
                        <TileLayer
                            key={currentTheme.value}
                            attribution={currentTheme.attribution}
                            url={currentTheme.url}
                        />
                    )}

                    <GeoJSON
                        key={boundaryType}
                        data={currentBoundary}
                        style={{ color: '#2502eb', weight: 1, fillColor: '#00ff73', fillOpacity: 0.07 }}
                    />

                    <FitLaosBounds geojson={currentBoundary} triggerKey={boundaryType} />

                    {/* Activity markers */}
                    {viewMode === 'activity' && !loading && filteredProjects.map(project => {
                        if (project.type === 'road') {
                            return (
                                <React.Fragment key={project.id}>
                                    <Polyline
                                        positions={project.gps}
                                        pathOptions={{ color: 'red', weight: 4, opacity: 1 }}
                                    >
                                        <Popup>
                                            <strong>{project.name}</strong><br />
                                            {project.address}<br />
                                            {lang === 'LA' ? 'ຄວາມຍາວ' : 'Length'}: {project.length} km
                                        </Popup>
                                    </Polyline>
                                    <Marker position={project.gps[0]} icon={redIcon}>
                                        <Popup>
                                            <strong>{project.name}</strong><br />
                                            {project.address}<br />
                                            {lang === 'LA' ? 'ຄວາມຍາວ' : 'Length'}: {project.length} km
                                        </Popup>
                                    </Marker>
                                </React.Fragment>
                            );
                        }
                        return (
                            <Marker key={project.id} position={project.gps} icon={greenIcon}>
                                <Popup>
                                    <strong>{project.name}</strong><br />
                                    {project.address}
                                </Popup>
                            </Marker>
                        );
                    })}

                    {/* Target village markers */}
                    {viewMode === 'villages' && !loadingTargetVillages && filteredVillages.map(village => (
                        <Marker key={`tv-${village.id}`} position={village.gps} icon={violetIcon}>
                            <Popup>
                                <strong>{village.village_name}</strong><br />
                                {village.district_name}, {village.province_name}
                            </Popup>
                        </Marker>
                    ))}

                    {/* Legend */}
                    <div className="map-legend">
                        <h4>{lang === 'LA' ? 'ຄຳອະທິບາຍ:' : 'Legend:'}</h4>
                        {viewMode === 'activity' && (
                            <>
                                <div className="legend-item">
                                    <img src={greenIconUrl} alt="Infrastructure" />
                                    {lang === 'LA' ? 'ພື້ນຖານໂຄງລ່າງ' : 'Infrastructure'}
                                </div>
                                <div className="legend-item">
                                    <img src={redIconUrl} alt="Road" />
                                    {lang === 'LA' ? 'ເສັ້ນທາງນ້ອຍ' : 'Access Track'}
                                </div>
                            </>
                        )}
                        {viewMode === 'villages' && (
                            <div className="legend-item">
                                <img src={violetIconUrl} alt="Target Village" />
                                {lang === 'LA' ? 'ບ້ານເປົ້າໝາຍ' : 'Target Village'}
                            </div>
                        )}
                    </div>
                </MapContainer>
            </div>
        </div>
    );
};

export default MapView;
